package com.example.borshi.web.account

import androidx.compose.runtime.*
import kotlinx.browser.window
import org.jetbrains.compose.web.attributes.InputType
import org.jetbrains.compose.web.css.*
import org.jetbrains.compose.web.dom.*

@Composable
fun PaymentsScreen(onBack: () -> Unit) {
    var paymentAmount by remember { mutableStateOf("") }
    var selectedMethod by remember { mutableStateOf<String?>(null) }
    var snackbar by remember { mutableStateOf<String?>(null) }

    Div({
        style {
            maxWidth(900.px)
            property("margin", "0 auto")
            padding(24.px, 16.px)
            width(100.percent)
            backgroundColor(Color("#F5F5F5"))
            minHeight(100.vh)
        }
    }) {
        // Header
        Div({
            style {
                display(DisplayStyle.Flex)
                justifyContent(JustifyContent.SpaceBetween)
                alignItems(AlignItems.Center)
                marginBottom(24.px)
            }
        }) {
            Div({
                style {
                    display(DisplayStyle.Flex)
                    alignItems(AlignItems.Center)
                    gap(16.px)
                }
            }) {
                Button({
                    style {
                        backgroundColor(Color("transparent"))
                        border(2.px, LineStyle.Solid, Color("#E0E0E0"))
                        padding(10.px, 16.px)
                        borderRadius(8.px)
                        cursor("pointer")
                        fontSize(14.px)
                        display(DisplayStyle.Flex)
                        alignItems(AlignItems.Center)
                        gap(8.px)
                    }
                    onClick { onBack() }
                }) {
                    Text("← Back")
                }

                H1({
                    style {
                        fontSize(28.px)
                        fontWeight("bold")
                        color(Color("#0D2B2E"))
                        margin(0.px)
                    }
                }) {
                    Text("Payments")
                }
            }
        }

        // Info Banner
        Div({
            style {
                backgroundColor(Color("#E3F2FD"))
                padding(16.px)
                borderRadius(12.px)
                marginBottom(24.px)
                display(DisplayStyle.Flex)
                gap(12.px)
                alignItems(AlignItems.Center)
            }
        }) {
            Span({ style { fontSize(28.px) } }) { Text("ℹ️") }
            P({
                style {
                    fontSize(15.px)
                    color(Color("#1976D2"))
                    margin(0.px)
                    lineHeight("1.6")
                }
            }) {
                Text("Make secure payments directly to Borshi using UPI apps, QR code, or pay on delivery")
            }
        }

        // Form Card
        Div({
            style {
                backgroundColor(Color.white)
                borderRadius(16.px)
                padding(24.px)
                property("box-shadow", "0 4px 12px rgba(0,0,0,0.08)")
                marginBottom(24.px)
            }
        }) {
            H2({
                style {
                    fontSize(20.px)
                    fontWeight("bold")
                    color(Color("#0D2B2E"))
                    marginBottom(16.px)
                }
            }) {
                Text("Enter Amount")
            }

            Input(InputType.Text) {
                value(paymentAmount)
                onInput { input ->
                    paymentAmount = input.value.filter { it.isDigit() || it == '.' }
                }
                attr("placeholder", "0.00")
                style {
                    width(100.percent)
                    padding(16.px)
                    fontSize(32.px)
                    fontWeight("700")
                    color(Color("#0D2B2E"))
                    borderRadius(12.px)
                    border(2.px, LineStyle.Solid, Color("#FFD1DC"))
                    backgroundColor(Color("#FFF7F9"))
                }
            }

            if (paymentAmount.isNotEmpty()) {
                P({
                    style {
                        marginTop(8.px)
                        fontSize(13.px)
                        color(Color("#757575"))
                        marginBottom(0.px)
                    }
                }) {
                    Text("Amount: ₹$paymentAmount")
                }
            }
        }

        // Payment Methods
        H3({
            style {
                fontSize(18.px)
                fontWeight("bold")
                color(Color("#212121"))
                marginBottom(12.px)
            }
        }) {
            Text("Select Payment Method")
        }

        val methods = listOf(
            Triple("GPay / UPI", "Pay instantly with any UPI app", "💳"),
            Triple("QR Code", "Scan with any UPI app", "📱"),
            Triple("Cash on Delivery", "Pay at your doorstep", "💵")
        )

        Div({
            style {
                display(DisplayStyle.Flex)
                flexDirection(FlexDirection.Column)
                gap(16.px)
            }
        }) {
            methods.forEach { (title, subtitle, icon) ->
                EnhancedPaymentMethodRow(
                    name = title,
                    subtitle = subtitle,
                    icon = icon,
                    selected = selectedMethod == title
                ) {
                    selectedMethod = title
                }
            }
        }

        if (selectedMethod == "QR Code") {
            QRCodePanel()
        }

        if (selectedMethod == "GPay / UPI" && paymentAmount.isNotBlank()) {
            UpiPayPanel(paymentAmount)
        }

        // Action Button
        Button({
            style {
                marginTop(24.px)
                width(100.percent)
                padding(16.px)
                backgroundColor(Color("#D11243"))
                color(Color.white)
                border(0.px)
                borderRadius(12.px)
                fontSize(18.px)
                fontWeight("700")
                cursor("pointer")
                property("box-shadow", "0 4px 16px rgba(209,18,67,0.3)")
            }
            onClick {
                val amount = paymentAmount.toDoubleOrNull()
                when {
                    amount == null || amount <= 0 -> snackbar = "Please enter a valid amount"
                    selectedMethod == null -> snackbar = "Please select a payment method"
                    selectedMethod == "Cash on Delivery" -> snackbar = "COD selected. Our team will confirm your order."
                    selectedMethod == "QR Code" -> snackbar = "Scan the QR code to pay ₹$paymentAmount"
                    selectedMethod == "GPay / UPI" -> snackbar = "Click 'Pay with UPI' button above to complete payment"
                }
            }
        }) {
            Text("Proceed to Pay")
        }

        snackbar?.let {
            P({
                style {
                    marginTop(12.px)
                    padding(12.px)
                    backgroundColor(Color("#FFF3E0"))
                    color(Color("#E65100"))
                    borderRadius(8.px)
                    fontWeight("600")
                }
            }) {
                Text(it)
            }
        }
    }
}

@Composable
private fun QRCodePanel() {
    Div({
        style {
            marginTop(20.px)
            backgroundColor(Color.white)
            padding(24.px)
            borderRadius(16.px)
            border(2.px, LineStyle.Solid, Color("#00CC66"))
            property("box-shadow", "0 4px 12px rgba(0,0,0,0.08)")
            textAlign("center")
        }
    }) {
        H3({
            style {
                fontSize(18.px)
                color(Color("#00CC66"))
                fontWeight("bold")
                marginBottom(8.px)
            }
        }) { Text("Scan QR Code to Pay") }

        P({
            style {
                fontSize(14.px)
                color(Color("#757575"))
                marginBottom(20.px)
            }
        }) { Text("Use any UPI app to scan and pay") }

        Img(src = "images/qr.jpg", attrs = {
            style {
                width(220.px)
                height(220.px)
                borderRadius(12.px)
                property("box-shadow", "0 4px 12px rgba(0,0,0,0.1)")
            }
        })

        P({
            style {
                marginTop(16.px)
                fontSize(13.px)
                color(Color("#757575"))
            }
        }) { Text("UPI ID: standarderror85@okhdfcbank") }
    }
}

@Composable
private fun UpiPayPanel(amount: String) {
    Div({
        style {
            marginTop(16.px)
            backgroundColor(Color("#FFF3E0"))
            padding(16.px)
            borderRadius(12.px)
            border(1.px, LineStyle.Solid, Color("#FFE0B2"))
        }
    }) {
        H4({
            style {
                fontSize(16.px)
                color(Color("#E65100"))
                fontWeight("bold")
                marginBottom(8.px)
            }
        }) { Text("Pay ₹$amount with UPI") }

        Button({
            style {
                width(100.percent)
                padding(12.px)
                backgroundColor(Color("#4CAF50"))
                color(Color.white)
                border(0.px)
                borderRadius(8.px)
                fontSize(16.px)
                fontWeight("700")
                cursor("pointer")
            }
            onClick {
                val upiUrl = "upi://pay?pa=standarderror85@okhdfcbank&pn=Borshi&tn=Payment&am=$amount&cu=INR"
                window.open(upiUrl, "_blank")
            }
        }) {
            Text("💳 Pay with UPI App")
        }

        P({
            style {
                marginTop(10.px)
                fontSize(12.px)
                color(Color("#757575"))
                textAlign("center")
            }
        }) { Text("After paying, return here and tap 'Proceed to Pay'") }
    }
}

@Composable
private fun EnhancedPaymentMethodRow(
    name: String,
    subtitle: String,
    icon: String,
    selected: Boolean,
    onSelect: () -> Unit
) {
    Div({
        style {
            display(DisplayStyle.Flex)
            alignItems(AlignItems.Center)
            gap(12.px)
            padding(12.px)
            borderRadius(12.px)
            border(2.px, LineStyle.Solid, if (selected) Color("#26A69A") else Color("#E0E0E0"))
            backgroundColor(if (selected) Color("#E0F2F1") else Color.white)
            cursor("pointer")
        }
        onClick { onSelect() }
    }) {
        Div({ style { fontSize(20.px) } }) { Text(icon) }
        Div({ style { flex(1) } }) {
            P({ style { margin(0.px); fontWeight("700"); color(Color("#0D2B2E")) } }) { Text(name) }
            P({ style { margin(0.px); color(Color("#666")); fontSize(13.px) } }) { Text(subtitle) }
        }
        if (selected) {
            P({ style { margin(0.px); color(Color("#26A69A")); fontWeight("700") } }) { Text("Selected") }
        }
    }
}
